import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Target, Calendar, TrendingUp, Settings2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { format, differenceInDays, startOfDay, startOfWeek } from "date-fns";

interface Goals {
  id?: string;
  daily_mcq_target: number;
  weekly_accuracy_target: number;
  exam_date: string | null;
}

const DEFAULTS: Goals = { daily_mcq_target: 20, weekly_accuracy_target: 70, exam_date: null };

export const PerformanceGoalsWidget = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Goals>(DEFAULTS);

  const { data: goals } = useQuery({
    queryKey: ["performance-goals", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_performance_goals")
        .select("*")
        .eq("user_id", user!.id)
        .maybeSingle();
      return (data as Goals | null) || null;
    },
    enabled: !!user,
  });

  const { data: progress } = useQuery({
    queryKey: ["goals-progress", user?.id],
    queryFn: async () => {
      const today = startOfDay(new Date()).toISOString();
      const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 }).toISOString();
      const [todayRes, weekRes] = await Promise.all([
        supabase.from("attempts").select("id, is_correct").eq("user_id", user!.id).gte("attempted_at", today),
        supabase.from("attempts").select("id, is_correct").eq("user_id", user!.id).gte("attempted_at", weekStart),
      ]);
      const todayAttempts = todayRes.data || [];
      const weekAttempts = weekRes.data || [];
      const weekCorrect = weekAttempts.filter((a) => a.is_correct).length;
      const accuracy = weekAttempts.length > 0 ? Math.round((weekCorrect / weekAttempts.length) * 100) : 0;
      return { todayCount: todayAttempts.length, weekAccuracy: accuracy, weekTotal: weekAttempts.length };
    },
    enabled: !!user,
  });

  const saveMutation = useMutation({
    mutationFn: async (g: Goals) => {
      const payload = {
        user_id: user!.id,
        daily_mcq_target: g.daily_mcq_target,
        weekly_accuracy_target: g.weekly_accuracy_target,
        exam_date: g.exam_date,
      };
      const { error } = await supabase.from("user_performance_goals").upsert(payload, { onConflict: "user_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Goals updated");
      qc.invalidateQueries({ queryKey: ["performance-goals"] });
      setOpen(false);
    },
    onError: (e: any) => toast.error(e.message || "Failed to save goals"),
  });

  const g = goals || DEFAULTS;
  const todayPct = Math.min(100, Math.round(((progress?.todayCount || 0) / g.daily_mcq_target) * 100));
  const accPct = Math.min(100, Math.round(((progress?.weekAccuracy || 0) / g.weekly_accuracy_target) * 100));
  const daysToExam = g.exam_date ? differenceInDays(new Date(g.exam_date), new Date()) : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Target className="h-4 w-4 text-primary" />
          My Goals
        </CardTitle>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft(g); }}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8">
              <Settings2 className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Set Performance Goals</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="daily">Daily MCQ target</Label>
                <Input id="daily" type="number" min={1} max={500} value={draft.daily_mcq_target}
                  onChange={(e) => setDraft({ ...draft, daily_mcq_target: parseInt(e.target.value) || 1 })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="acc">Weekly accuracy target (%)</Label>
                <Input id="acc" type="number" min={0} max={100} value={draft.weekly_accuracy_target}
                  onChange={(e) => setDraft({ ...draft, weekly_accuracy_target: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exam">Target exam date (optional)</Label>
                <Input id="exam" type="date" value={draft.exam_date || ""}
                  onChange={(e) => setDraft({ ...draft, exam_date: e.target.value || null })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => saveMutation.mutate(draft)} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Today's MCQs
            </span>
            <span className="font-medium">{progress?.todayCount || 0} / {g.daily_mcq_target}</span>
          </div>
          <Progress value={todayPct} className="h-2" />
        </div>

        <div>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <TrendingUp className="h-3.5 w-3.5" />
              Weekly accuracy
            </span>
            <span className="font-medium">
              {progress?.weekAccuracy || 0}% / {g.weekly_accuracy_target}%
              {progress?.weekTotal === 0 && <span className="text-xs text-muted-foreground ml-1">(no data)</span>}
            </span>
          </div>
          <Progress value={accPct} className="h-2" />
        </div>

        {g.exam_date && daysToExam !== null && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <div>
                <p className="text-sm font-medium">Exam</p>
                <p className="text-xs text-muted-foreground">{format(new Date(g.exam_date), "PPP")}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">{daysToExam >= 0 ? daysToExam : 0}</p>
              <p className="text-xs text-muted-foreground">days left</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PerformanceGoalsWidget;
